<template>
  <NuxtLayout>
    <div class="text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="onHeaderSearchClick"
        @menu-click="onHeaderMenuClick"
        @login="onHeaderLogin"
        @how-it-works-click="onHowItWorks"
      />
      <div
        class="mx-auto w-full max-w-[1366px] px-4 pt-4 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <button class="flex flex-row items-center gap-1" @click="onBack">
          <MdiIcon :size="'1.5rem'" icon="mdiChevronLeft" /><span>{{
            $t("establishment.back")
          }}</span>
        </button>
      </div>
      <div
        class="mx-auto flex w-full max-w-[1366px] flex-1 flex-col items-start px-4 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <div
          v-if="estError"
          class="mb-4 w-full rounded bg-red-50 p-4 text-red-700"
        >
          {{ $t("establishment.failedToLoad") }}
        </div>
        <!-- <button @click="onBack" class="flex flex-row items-center gap-1 ">
                    <MdiIcon :size="'1.5rem'" icon="mdiChevronLeft" /><span>Back</span>
                </button> -->
        <div
          class="mb-3 flex w-full flex-1 flex-col items-start justify-between gap-3 sm:flex-row sm:items-center"
        >
          <div class="my-2">
            <h1 v-if="!estPending" class="text-2xl font-bold">
              {{ companyName }}
            </h1>
            <div
              v-else
              class="h-8 w-56 animate-pulse rounded-md bg-gray-200"
              aria-label="Loading establishment name"
            ></div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <NuxtLink
              :to="newsletterPath"
              class="inline-flex items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-4 py-2 text-sm font-medium text-pink-600 transition-colors hover:bg-pink-100"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8m-2 10H5a2 2 0 01-2-2V8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z"
                />
              </svg>
              {{ $t("newsletter.subscribe.cta") }}
            </NuxtLink>
            <button
              class="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-[#2e234b]"
              @click="isShareModalOpen = true"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              {{ $t("share.title") }}
            </button>
          </div>
        </div>

        <!-- Image Gallery - component -->
        <ImageGallery
          :images="imagesList"
          :alt-prefix="companyName"
          fallback-image="/images/placeholder-soft-neutral-venue.svg"
        />
        <div class="mt-4 flex w-full flex-row justify-between md:w-3/4">
          <p v-if="!estPending" class="text-sm font-medium">
            {{ companyEventsCount }} {{ $t("establishment.events") }}
          </p>
          <div
            v-else
            class="h-4 w-28 animate-pulse rounded bg-gray-200"
            aria-label="Loading event count"
          ></div>
          <!-- <p>{{ companyFollowers }}</p> -->
        </div>
        <div class="mt-4">
          <h2 class="sr-only">About {{ companyName }}</h2>
          <div v-if="!estPending">
            <p>{{ companyDescription }}</p>
          </div>
          <div
            v-else
            class="space-y-2"
            aria-label="Loading establishment description"
          >
            <div class="h-4 w-full animate-pulse rounded bg-gray-200"></div>
            <div class="h-4 w-11/12 animate-pulse rounded bg-gray-200"></div>
            <div class="h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
        <div v-if="categoryNamesList.length > 0" class="mt-4">
          <h3 class="mb-2 text-sm font-semibold">
            {{ $t("establishment.categories") }}
          </h3>
          <div class="flex flex-wrap items-center gap-2">
            <div
              v-for="(cat, i) in categoryNamesList"
              :key="i"
              class="inline-flex items-center gap-1.5 rounded-full border border-purple-100 bg-purple-50 px-3 py-1.5"
            >
              <Icon
                class="h-3.5 w-3.5 flex-shrink-0 text-purple-950"
                :name="cat"
              />
              <span
                class="whitespace-nowrap text-xs font-medium text-purple-950"
                :title="cat"
                >{{ cat }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <section
        class="mx-auto my-12 w-full max-w-[1366px] px-4 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <!-- Event Tabs -->
        <div class="mb-4 overflow-x-auto border-b border-gray-200">
          <div class="flex min-w-max">
            <button
              :class="[
                'whitespace-nowrap px-3 py-2.5 text-sm font-semibold tracking-wide transition-colors md:px-5',
                activeTab === 'upcoming'
                  ? 'border-b-2 border-pink-500 text-pink-500'
                  : 'text-gray-500 hover:text-purple-950',
              ]"
              @click="activeTab = 'upcoming'"
            >
              {{ $t("establishment.upcomingEvents") }}
              <span
                v-if="upcomingEvents.length > 0"
                class="ml-1.5 rounded-full bg-pink-100 px-1.5 py-0.5 text-xs text-pink-600"
                >{{ upcomingEvents.length }}</span
              >
            </button>
            <button
              :class="[
                'whitespace-nowrap px-3 py-2.5 text-sm font-semibold tracking-wide transition-colors md:px-5',
                activeTab === 'past'
                  ? 'border-b-2 border-pink-500 text-pink-500'
                  : 'text-gray-500 hover:text-purple-950',
              ]"
              @click="activeTab = 'past'"
            >
              {{ $t("establishment.pastEvents") }}
              <span
                v-if="pastEvents.length > 0"
                class="ml-1.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-xs text-gray-500"
                >{{ pastEvents.length }}</span
              >
            </button>
            <button
              v-if="canceledEvents.length > 0"
              :class="[
                'whitespace-nowrap px-3 py-2.5 text-sm font-semibold tracking-wide transition-colors md:px-5',
                activeTab === 'canceled'
                  ? 'border-b-2 border-pink-500 text-pink-500'
                  : 'text-gray-500 hover:text-purple-950',
              ]"
              @click="activeTab = 'canceled'"
            >
              {{ $t("establishment.canceledEvents") }}
              <span
                class="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-xs text-red-500"
                >{{ canceledEvents.length }}</span
              >
            </button>
          </div>
        </div>
        <CardGrid
          v-if="activeTab === 'upcoming'"
          title=""
          :items="upcomingEvents"
          :has-more="false"
          :loading="initialEventsLoading"
          :single-column-on-mobile="true"
        />
        <div
          v-if="
            activeTab === 'upcoming' &&
            upcomingEvents.length === 0 &&
            !initialEventsLoading
          "
          class="py-12 text-center text-sm text-gray-400"
        >
          {{ $t("establishment.noUpcomingEvents") }}
        </div>
        <CardGrid
          v-if="activeTab === 'past'"
          title=""
          :items="pastEvents"
          :has-more="false"
          :loading="initialEventsLoading"
          :single-column-on-mobile="true"
        />
        <div
          v-if="
            activeTab === 'past' &&
            pastEvents.length === 0 &&
            !initialEventsLoading
          "
          class="py-12 text-center text-sm text-gray-400"
        >
          {{ $t("establishment.noPastEvents") }}
        </div>
        <CardGrid
          v-if="activeTab === 'canceled'"
          title=""
          :items="canceledEvents"
          :has-more="false"
          :loading="initialEventsLoading"
          :single-column-on-mobile="true"
        />
        <div
          v-if="
            activeTab === 'canceled' &&
            canceledEvents.length === 0 &&
            !initialEventsLoading
          "
          class="py-12 text-center text-sm text-gray-400"
        >
          {{ $t("establishment.noCanceledEvents") }}
        </div>
      </section>

      <!-- Ad Slot: between event listings and map -->
      <div
        class="mx-auto w-full max-w-[1366px] px-4 pb-6 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
        aria-label="Advertisement"
        role="complementary"
      >
        <!-- <span class="text-[10px] uppercase tracking-wider text-gray-400"
          >Sponsored</span
        > -->
        <!-- <div class="mt-1">
          <ClientOnly>
            <AdsAdUnit slot-id="ESTABLISHMENT_PRE_MAP_SLOT_ID" />
          </ClientOnly>
        </div> -->
      </div>

      <section
        class="mx-auto w-full max-w-[1366px] px-4 pb-12 sm:px-5 md:px-[15vw] lg:px-[12vw] xl:px-[12vw]"
      >
        <h2 class="my-6 text-xl font-bold">
          {{ $t("establishment.locationTitle") }}
        </h2>
        <div v-if="formattedAddressLines.length" class="space-y-0">
          <p
            v-for="(line, idx) in formattedAddressLines"
            :key="idx"
            class="text-base"
          >
            {{ line }}
          </p>
        </div>
        <h3 v-else>Address Data correctly formatted</h3>
        <ClientOnly>
          <div class="mt-4">
            <LeafletMap
              v-if="lat !== null && lon !== null"
              :lat="lat"
              :lon="lon"
              :aria-label="`Map showing location of ${companyName}`"
            />
            <div v-else class="text-sm text-gray-500">
              {{ $t("establishment.locationNotAvailable") }}
            </div>
          </div>
        </ClientOnly>
      </section>

      <ShareModal
        v-model:show="isShareModalOpen"
        :title="companyName"
        :description="companyDescription"
        :image-url="ogImage"
        :url="canonicalUrl"
        :location="shareLocation"
      />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, watch, onMounted } from "vue";
import AppHeader from "~/components/header/AppHeader.vue";
import ImageGallery from "~/components/image-gallery/ImageGallery.vue";
import LeafletMap from "~/components/map/LeafletMap.vue";
import CardGrid from "~/components/card-grid/CardGrid.vue";
import Icon from "~/components/Icon.vue";
import ShareModal from "~/components/share/ShareModal.vue";
import { ApiClient } from "~~/shared/api/ApiClient";
import type { ApiClientOptions } from "~~/shared/api/ApiClient";
import { useApiBaseUrl } from "~~/shared/models";
import type {
  CategoryResponse,
  ListImageEstablishmentResponse,
} from "~~/shared/models";
import { EstablishmentService } from "~~/shared/services/establishment";
import { AddressService } from "~~/shared/services/address";
import { useAnalytics } from "~/composables/useAnalytics";
import { extractUuidFromSlug, makeSlug } from "~~/shared/utils/slugUtils";
import { isPublicDiscoveryRecord } from "~~/shared/utils/eventDiscovery";
import type { PublicEstablishmentDetailResponse } from "~~/shared/models/public-detail";
import {
  buildBreadcrumbSchema,
  buildVenueSchema,
} from "~~/shared/utils/seo/entitySchemas";
import { jsonLdHeadScript } from "~~/shared/utils/seo/jsonLd";
import {
  resolveEventTimeZoneCandidate,
  toEventDate,
} from "~~/shared/utils/seo/eventTime";
import { isPublicVenueDetail } from "~~/shared/utils/entityAccess";
import { setResponseHeader } from "h3";

const { trackOpened, emit } = useAnalytics();
const { translateCategory } = useCategoryTranslation();
const { t } = useI18n();
type Mode = "event" | "establishment";
const opts: ApiClientOptions = {
  baseUrl: useApiBaseUrl(),
};
const client = new ApiClient(opts);

const establishmentService = new EstablishmentService(client);
const addressService = new AddressService(client);

const activeTab = ref<"upcoming" | "past" | "canceled">("upcoming");

const route = useRoute();
const router = useRouter();
const venueRenderNow = useHydrationClock(`venue:${route.path}`);
const localePath = useLocalePath();
const serverRequestEvent = import.meta.server ? useRequestEvent() : undefined;
const { APP_URL } = usePublicUrls();
const {
  fallbackImagePath: socialFallbackImage,
  normalizeMetaText,
  toAbsolutePublicUrl,
} = useSocialSeo();
const SOCIAL_FALLBACK_IMAGE = socialFallbackImage;

type VenueEventCard = {
  id: string;
  type: "event";
  eventName: string;
  date: string;
  start?: string;
  startDate?: string;
  end?: string;
  endDate?: string;
  timeZone?: string;
  timezone?: string;
  time_zone?: string;
  status?: string;
  visibility?: string;
  category?: string[];
  imageUrl?: string | null;
  isCanceled?: boolean;
  is_canceled?: boolean;
  canceled?: boolean;
  cancelled?: boolean;
  [key: string]: unknown;
};

const events = ref<VenueEventCard[]>([]);

function isEventCanceled(ev: VenueEventCard): boolean {
  const flag = ev.isCanceled ?? ev.is_canceled ?? ev.canceled ?? ev.cancelled;
  if (flag === true) return true;
  const status = (ev.status ?? "").toLowerCase();
  return status === "canceled" || status === "cancelled";
}

const canceledEvents = computed(() => {
  return events.value.filter((ev) => isEventCanceled(ev));
});

const timedEvents = computed(() =>
  events.value.flatMap((event) => {
    if (isEventCanceled(event)) return [];
    if ((event.visibility ?? "public") === "private") return [];

    const timeZone = resolveEventTimeZoneCandidate(
      event.timeZone,
      event.timezone,
      event.time_zone,
    );
    if (!timeZone) return [];
    const startRaw = event.start ?? event.startDate ?? event.date ?? null;
    const endRaw = event.endDate ?? event.end ?? event.date ?? null;
    const startMs = startRaw
      ? (toEventDate(startRaw, timeZone)?.getTime() ?? null)
      : null;
    const endMs = endRaw
      ? (toEventDate(endRaw, timeZone)?.getTime() ?? null)
      : null;
    return startMs === null || endMs === null
      ? []
      : [{ event, startMs, endMs }];
  }),
);

const upcomingEvents = computed(() => {
  const now = venueRenderNow.value;
  return timedEvents.value
    .filter(({ endMs }) => endMs >= now)
    .sort((a, b) => {
      const aInProgress = a.startMs <= now && now <= a.endMs;
      const bInProgress = b.startMs <= now && now <= b.endMs;
      if (aInProgress !== bInProgress) return aInProgress ? -1 : 1;

      if (a.startMs !== b.startMs) return a.startMs - b.startMs;

      return a.endMs - b.endMs;
    })
    .map(({ event }) => event);
});

const pastEvents = computed(() => {
  const now = venueRenderNow.value;
  return timedEvents.value
    .filter(({ endMs }) => endMs < now)
    .sort((a, b) => {
      // most recent past event first
      return b.endMs - a.endMs;
    })
    .map(({ event }) => event);
});

const EVENT_PAGE_SIZE = 24;
const MAX_BFF_EVENT_PAGES = 250;

type EventsBffResponse = {
  events: VenueEventCard[];
  hasMore: boolean;
};

async function listAllEstablishmentEvents(establishmentUuid: string) {
  const publicEvents: VenueEventCard[] = [];
  const seen = new Set<string>();

  for (
    let requestedPage = 1;
    requestedPage <= MAX_BFF_EVENT_PAGES;
    requestedPage += 1
  ) {
    const response = await $fetch<EventsBffResponse>("/bff/events", {
      query: {
        establishmentId: establishmentUuid,
        page: requestedPage,
        limit: EVENT_PAGE_SIZE,
      },
    });

    for (const eventCard of response.events ?? []) {
      const id = String(eventCard?.id ?? "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      publicEvents.push({
        ...eventCard,
        category: (eventCard.category ?? []).map(translateCategory),
        imageUrl: eventCard.imageUrl || "/images/hero.jpg",
        visibility: "public",
      });
    }

    if (!response.hasMore) {
      return { events: publicEvents, total: publicEvents.length };
    }
  }

  throw new Error(
    "Public establishment event inventory exceeded the bounded BFF pagination",
  );
}

const images = ["/images/placeholder-soft-neutral-venue.svg"];

// Header handlers
const mode = ref<Mode>("establishment");
function onModeChange(_next: Mode) {
  // AppHeader handles navigation automatically
}
function onHeaderSearchClick() {}
function onHeaderMenuClick() {}
function onHeaderLogin() {
  window.location.href = `${APP_URL}/`;
}
function onHowItWorks() {}

function onBack() {
  if (import.meta.client && window.history.length > 1) {
    router.back();
  } else {
    // fallback to home with establishment mode
    router.push({ path: localePath("/"), query: { mode: "establishment" } });
  }
}

function setNoStoreResponseHeader() {
  if (serverRequestEvent) {
    setResponseHeader(serverRequestEvent, "Cache-Control", "no-store");
  }
}

function throwPublicBffError(error: unknown, resource: "Establishment"): never {
  const source = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
    data?: { statusCode?: unknown };
  };
  const upstreamStatus = Number(
    source?.statusCode ??
      source?.status ??
      source?.response?.status ??
      source?.data?.statusCode,
  );
  const statusCode =
    upstreamStatus === 404 || upstreamStatus === 410 ? upstreamStatus : 503;
  if (statusCode === 503) setNoStoreResponseHeader();
  throw createError({
    statusCode,
    statusMessage:
      statusCode === 410
        ? `${resource} permanently removed`
        : statusCode === 404
          ? `${resource} not found`
          : "Service unavailable",
    fatal: true,
  });
}

// Resolve only strict UUID/slug identifiers. Invalid routes must not reach the API.
const establishmentSlug = String(route.params.establishmentSlug ?? "");
const resolvedEstablishmentId = extractUuidFromSlug(establishmentSlug);
if (!resolvedEstablishmentId) {
  throw createError({
    statusCode: 404,
    statusMessage: "Establishment not found",
    fatal: true,
  });
}

const { data: establishmentEntity, error: establishmentEntityError } =
  await useAsyncData(
    `establishment-entity-${resolvedEstablishmentId}`,
    async () => {
      try {
        const response = await $fetch<PublicEstablishmentDetailResponse>(
          `/bff/public/establishments/${resolvedEstablishmentId}`,
        );
        return response.establishment;
      } catch (error) {
        throwPublicBffError(error, "Establishment");
      }
    },
  );
if (establishmentEntityError.value) throw establishmentEntityError.value;

const resolvedEstablishmentSource = establishmentEntity.value;
if (
  !resolvedEstablishmentSource?.id ||
  String(resolvedEstablishmentSource.id).toLowerCase() !==
    resolvedEstablishmentId ||
  !resolvedEstablishmentSource.name?.trim()
) {
  setNoStoreResponseHeader();
  throw createError({
    statusCode: 503,
    statusMessage: "Invalid establishment response",
    fatal: true,
  });
}
if (!isPublicVenueDetail(resolvedEstablishmentSource)) {
  throw createError({
    statusCode: 404,
    statusMessage: "Establishment not found",
    fatal: true,
  });
}
const resolvedEstablishment = resolvedEstablishmentSource;

const canonicalEstablishmentSlug = makeSlug(
  resolvedEstablishment.name,
  resolvedEstablishment.id,
);
const canonicalEstablishmentRoute = localePath(
  `/establishment/${canonicalEstablishmentSlug}`,
);
const needsCanonicalRedirect = route.path !== canonicalEstablishmentRoute;
if (needsCanonicalRedirect) {
  await navigateTo(
    {
      path: canonicalEstablishmentRoute,
      query: route.query,
      hash: route.hash,
    },
    { redirectCode: 301, replace: true },
  );
}

// Enrichment runs only after aliases have been redirected to the canonical route.
const {
  data: estData,
  pending: estPending,
  error: estError,
} = await useAsyncData(
  `establishment-page-${resolvedEstablishmentId}`,
  async () => {
    if (import.meta.server && needsCanonicalRedirect) {
      return {
        establishment: resolvedEstablishment,
        address: null,
        city: null,
        country: null,
        categories: [],
        images: [],
        events: [],
        total: 0,
        hasMore: false,
        loadedPages: 0,
      };
    }

    try {
      const establishment = resolvedEstablishment;
      const address = establishment?.addressId
        ? await addressService.fetchAddress(establishment.addressId)
        : null;
      const city = address?.cityId
        ? await addressService.fetchCity(address.cityId)
        : null;
      const country = address?.countryId
        ? await addressService.fetchCountry(address.countryId)
        : null;

      const categories: CategoryResponse[] = [];
      for (const id of establishment.categoriesId ?? []) {
        try {
          const cat = await establishmentService.fetchCategory(id);
          categories.push(cat);
        } catch (e) {
          console.warn("fetchCategory failed", id, e);
        }
      }

      // ImageGallery expects an array of string URLs.
      let imagesRes: string[] = [];
      try {
        const resp: ListImageEstablishmentResponse =
          await establishmentService.listImagesFromEstablishment(
            establishment.id,
          );

        imagesRes = (resp.images ?? [])
          .map((img: unknown) => {
            if (!img) return null;
            if (typeof img === "string") return img;
            if (!isPublicDiscoveryRecord(img)) return null;
            const image = img as Record<string, unknown>;
            return String(image.URL ?? image.url ?? "").trim() || null;
          })
          .filter((url): url is string => Boolean(url));
      } catch (e) {
        console.warn("fetching establishment images failed", e);
      }

      const eventList = await listAllEstablishmentEvents(establishment.id);

      return {
        establishment,
        address,
        city,
        country,
        categories,
        images: imagesRes,
        events: eventList.events,
        total: eventList.total,
      };
    } catch (error) {
      setNoStoreResponseHeader();
      throw createError({
        statusCode: 503,
        statusMessage: "Establishment dependencies unavailable",
        fatal: true,
        cause: error,
      });
    }
  },
);
if (estError.value) throw estError.value;

// Wire async data into local refs used by the template/components
watchEffect(() => {
  if (estData.value) {
    events.value = estData.value.events ?? [];
  } else {
    events.value = [];
  }
});

const initialEventsLoading = computed(
  () => Boolean(estPending.value) && events.value.length === 0,
);

// computed fallbacks used in template
const est = computed(() => estData.value?.establishment ?? null);
const companyName = computed(() => {
  return est.value?.name ?? "";
});
const companyDescription = computed(() => est.value?.description ?? "");
const companyEventsCount = computed(() => {
  return String(events.value.length);
});
// const companyFollowers = computed(() => (est.value?.followers !== undefined) ? String(est.value.followers) : '-company-followers');
const imagesList = computed(() =>
  estData.value?.images && estData.value.images.length > 0
    ? estData.value.images
    : images,
);

// location refs and formatted address
const lat = ref<number | null>(null);
const lon = ref<number | null>(null);

watchEffect(() => {
  const a = estData.value?.address ?? null;
  // project address model exposes `latitude` and `longitude` (numbers)
  const maybeLat = a?.latitude ?? null;
  const maybeLon = a?.longitude ?? null;
  lat.value =
    typeof maybeLat === "number"
      ? maybeLat
      : maybeLat
        ? Number(maybeLat)
        : null;
  lon.value =
    typeof maybeLon === "number"
      ? maybeLon
      : maybeLon
        ? Number(maybeLon)
        : null;
});

const formattedAddressLines = computed(() => {
  const a = estData.value?.address;
  if (!a) return [] as string[];
  const address = a as unknown as Record<string, unknown>;

  // Preferred model: streetOne, streetTwo, postalCode, cityName, countryName
  const streetParts: string[] = [];
  if (a.streetOne) streetParts.push(a.streetOne);
  if (a.streetTwo) streetParts.push(a.streetTwo);
  const streetLine = streetParts.join(" ").trim();

  const cityName = estData.value?.city?.cityName ?? "";
  const postal = a.postalCode ?? "";
  const cityLine = [postal, cityName].filter(Boolean).join(" ").trim();

  const countryName = estData.value?.country?.countryName ?? "";

  const lines: string[] = [];
  if (streetLine) lines.push(streetLine);
  if (cityLine) lines.push(cityLine);
  if (countryName) lines.push(countryName);

  // Legacy fallback mapping to 3 lines: road / postcode + city / country
  if (lines.length === 0) {
    const road = String(address.road ?? "");
    const postcode = String(address.postcode ?? "");
    const city = String(address.city ?? "");
    const country = String(address.country ?? "");
    if (road) lines.push(road);
    const pcCity = [postcode, city].filter(Boolean).join(" ").trim();
    if (pcCity) lines.push(pcCity);
    if (country) lines.push(country);
  }

  return lines.slice(0, 3);
});

// SEO: Dynamic meta tags, Open Graph, Twitter Cards
const cityName = computed(() => estData.value?.city?.cityName ?? "");
const countryName = computed(() => estData.value?.country?.countryName ?? "");
const categoryNamesList = computed(() =>
  (estData.value?.categories ?? []).map((c) => translateCategory(c.name)),
);

const socialTitle = computed(() => `${companyName.value} - Upfest`);

const socialMetaDescription = computed(() => {
  const location = [cityName.value, countryName.value]
    .filter(Boolean)
    .join(", ");
  const eventsLabel = companyEventsCount.value
    ? t("establishment.eventCount", { count: companyEventsCount.value })
    : "";
  const snippet = normalizeMetaText(companyDescription.value).slice(0, 120);

  const details = [location, eventsLabel].filter(Boolean).join(" · ");
  const combined = snippet
    ? `${details}${details ? " · " : ""}${snippet}`
    : details;

  return (combined || `${companyName.value} - Upfest`).slice(0, 200);
});

const pageTitle = computed(() => {
  const name = companyName.value || t("establishment.fallbackName");
  const location = cityName.value
    ? t("establishment.seoLocation", { city: cityName.value })
    : "";
  return t("establishment.seoTitle", { name, location });
});

const metaDescription = computed(() =>
  socialMetaDescription.value.slice(0, 160),
);

const establishmentPath = computed(() => {
  return `/establishment/${canonicalEstablishmentSlug}`;
});

const canonicalPath = computed(() => localePath(establishmentPath.value));
const canonicalUrl = computed(() => toAbsolutePublicUrl(canonicalPath.value));
const alternateEnUrl = computed(() =>
  toAbsolutePublicUrl(localePath(establishmentPath.value, "en")),
);
const alternateDeUrl = computed(() =>
  toAbsolutePublicUrl(localePath(establishmentPath.value, "de")),
);

const ogImage = computed(() => {
  const establishmentRecord = est.value as unknown as Record<
    string,
    unknown
  > | null;
  const establishmentMetaImage = [
    establishmentRecord?.ogImageUrl,
    establishmentRecord?.og_image_url,
    establishmentRecord?.socialImageUrl,
    establishmentRecord?.social_image_url,
    establishmentRecord?.coverImageUrl,
    establishmentRecord?.cover_image_url,
    establishmentRecord?.bannerImageUrl,
    establishmentRecord?.banner_image_url,
  ].find(
    (candidate) => typeof candidate === "string" && candidate.trim().length > 0,
  ) as string | undefined;

  const firstGalleryImage = imagesList.value.find(
    (img) => typeof img === "string" && img.trim().length > 0,
  ) as string | undefined;

  return toAbsolutePublicUrl(
    establishmentMetaImage ?? firstGalleryImage ?? SOCIAL_FALLBACK_IMAGE,
  );
});

const newsletterPath = computed(() => {
  return localePath(`/establishment/${canonicalEstablishmentSlug}/newsletter`);
});

useSeoMeta({
  title: pageTitle,
  description: metaDescription,
  ogTitle: socialTitle,
  ogDescription: socialMetaDescription,
  ogImage: ogImage,
  ogUrl: canonicalUrl,
  ogType: "website",
  ogSiteName: "Upfest",
  twitterCard: "summary_large_image",
  twitterTitle: socialTitle,
  twitterDescription: socialMetaDescription,
  twitterImage: ogImage,
});

useHead(
  computed(() => ({
    link: [
      { rel: "canonical", href: canonicalUrl.value },
      { rel: "alternate", hreflang: "en", href: alternateEnUrl.value },
      { rel: "alternate", hreflang: "de", href: alternateDeUrl.value },
      { rel: "alternate", hreflang: "x-default", href: alternateEnUrl.value },
    ],
  })),
);

// Share modal
const isShareModalOpen = ref(false);

const shareLocation = computed(() => {
  const parts = [cityName.value, countryName.value].filter(Boolean);
  return parts.join(", ");
});

// SEO: JSON-LD Structured Data
const structuredData = computed(() => {
  const schemas: Record<string, unknown>[] = [];

  if (estData.value?.establishment) {
    const a = estData.value?.address;
    const streetAddress = [a?.streetOne, a?.streetTwo]
      .filter(Boolean)
      .join(" ");
    const localBusiness = buildVenueSchema({
      name: companyName.value,
      canonicalUrl: canonicalUrl.value,
      description: companyDescription.value,
      address: a
        ? {
            streetAddress,
            postalCode: a.postalCode,
            addressLocality: cityName.value,
            addressRegion: estData.value?.city?.stateProvince,
            addressCountry: countryName.value,
          }
        : null,
      geo: { latitude: lat.value, longitude: lon.value },
      imageUrls: (estData.value.images ?? [])
        .filter(
          (image): image is string =>
            typeof image === "string" && Boolean(image.trim()),
        )
        .map(toAbsolutePublicUrl),
    });
    if (localBusiness) schemas.push(localBusiness);
  }

  schemas.push(
    buildBreadcrumbSchema([
      { name: t("nav.home"), url: toAbsolutePublicUrl(localePath("/")) },
      { name: companyName.value, url: canonicalUrl.value },
    ]),
  );

  return schemas;
});

useHead(
  computed(() => ({
    script: structuredData.value.map((schema, index) =>
      jsonLdHeadScript(`establishment-jsonld-${index}`, schema),
    ),
  })),
);

// Track that the establishment page was opened
onMounted(() => {
  watch(
    () => est.value?.id,
    (id) => {
      if (id)
        emit("detail_viewed", {
          content_type: "establishment",
          establishment_id: id,
        });
    },
    { immediate: true },
  );
  const estId = est.value?.id;
  if (estId) {
    trackOpened("establishment", estId);
  }
});

// If data loads after mount (SSR hydration), fire the opened event then
watch(
  () => est.value?.id,
  (id) => {
    if (id) trackOpened("establishment", id);
  },
  { once: true },
);
</script>

<style scoped>
/* Make tiles grayscale like MapPicker */
:deep(.leaflet-tile) {
  filter: grayscale(100%) contrast(0.95) brightness(0.95) !important;
}

/* Custom pin styles */
:deep(.custom-pin) {
  display: inline-block;
  line-height: 0;
}

:deep(.custom-pin svg) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
}

/* Ensure map container fills area */
.leaflet-container {
  height: 100%;
  width: 100%;
}
</style>
